import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { ProductManagementSettings } from "@/lib/types";
import { DEFAULT_COMMUNICATION_TEMPLATES } from "@/lib/communicationTemplateService";

// Cache for product settings with TTL
interface SettingsCache {
  data: ProductManagementSettings;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let settingsCache: SettingsCache | null = null;

// Track if a fetch is in progress to prevent duplicate requests
let fetchInProgress = false;
let fetchPromise: Promise<ProductManagementSettings> | null = null;

/**
 * Internal function to actually fetch settings from API
 */
async function fetchSettingsFromAPI(): Promise<ProductManagementSettings> {
  // Check cache first
  const now = Date.now();
  if (settingsCache && now - settingsCache.timestamp < CACHE_DURATION) {
    return settingsCache.data;
  }

  let session = (await supabase.auth.getSession()).data.session;

  // If no session, try refreshing
  if (!session) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    session = refreshData.session ?? null;
  }

  // Use defaults if still no session (non-admin users or not logged in)
  if (!session) {
    return {
      shareTextTemplate: {
        type: "option_a",
        locationMode: "full",
      },
      communicationTemplates: DEFAULT_COMMUNICATION_TEMPLATES,
    };
  }

  try {
    const response = await fetch("/api/admin/product-settings", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      cache: "force-cache",
      next: { revalidate: 300 }, // Revalidate every 5 minutes
    });

    if (response.ok) {
      const data = await response.json();
      const fetchedSettings: ProductManagementSettings = data.settings || {
        shareTextTemplate: {
          type: "option_a",
          locationMode: "full",
        },
        communicationTemplates: DEFAULT_COMMUNICATION_TEMPLATES,
      };

      // Update cache
      settingsCache = {
        data: fetchedSettings,
        timestamp: now,
      };

      return fetchedSettings;
    }
  } catch (error) {
    console.error("Error fetching product settings:", error);
  }

  // Use defaults on error
  return {
    shareTextTemplate: {
      type: "option_a",
      locationMode: "full",
    },
    communicationTemplates: DEFAULT_COMMUNICATION_TEMPLATES,
  };
}

export function useProductSettings(enableAutoFetch: boolean = true) {
  const [settings, setSettings] = useState<ProductManagementSettings>({
    shareTextTemplate: {
      type: "option_a",
    },
    communicationTemplates: DEFAULT_COMMUNICATION_TEMPLATES,
  });
  const [isLoading, setIsLoading] = useState(enableAutoFetch);

  const fetchSettings = useCallback(async () => {
    // If already in progress, wait for the existing fetch
    if (fetchInProgress && fetchPromise) {
      const cachedSettings = await fetchPromise;
      setSettings(cachedSettings);
      setIsLoading(false);
      return;
    }

    // Check cache immediately
    const now = Date.now();
    if (settingsCache && now - settingsCache.timestamp < CACHE_DURATION) {
      setSettings(settingsCache.data);
      setIsLoading(false);
      return;
    }

    // Mark as in progress and create promise
    fetchInProgress = true;
    fetchPromise = fetchSettingsFromAPI();

    try {
      const fetchedSettings = await fetchPromise;
      setSettings(fetchedSettings);
    } catch (error) {
      console.error("Error in fetchSettings:", error);
      // Use defaults on error
      setSettings({
        shareTextTemplate: {
          type: "option_a",
          locationMode: "full",
        },
        communicationTemplates: DEFAULT_COMMUNICATION_TEMPLATES,
      });
    } finally {
      setIsLoading(false);
      fetchInProgress = false;
      fetchPromise = null;
    }
  }, []);

  useEffect(() => {
    if (enableAutoFetch) {
      const timer = setTimeout(() => {
        void fetchSettings();
      }, 0);
      return () => clearTimeout(timer);
    } else {
      // If auto-fetch is disabled, just check cache
      const now = Date.now();
      const cachedSnapshot = settingsCache;
      if (cachedSnapshot && now - cachedSnapshot.timestamp < CACHE_DURATION) {
        const timer = setTimeout(() => {
          setSettings(cachedSnapshot.data);
          setIsLoading(false);
        }, 0);
        return () => clearTimeout(timer);
      }
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [enableAutoFetch, fetchSettings]);

  const refreshSettings = useCallback(async () => {
    settingsCache = null; // Invalidate cache
    fetchInProgress = false; // Reset fetch in progress
    fetchPromise = null;
    await fetchSettings();
  }, [fetchSettings]);

  return { settings, isLoading, refreshSettings };
}

// Function to invalidate cache (useful when settings are updated)
export function invalidateProductSettingsCache() {
  settingsCache = null;
  fetchInProgress = false;
  fetchPromise = null;
}
